import type { ReactNode } from 'react'
import { useUnitStore } from '@/stores/unitStore'

type Props = {
  children: ReactNode
  /** Friendly label for the empty state, e.g. "o fechamento diário". Default: "esta página". */
  label?: string
}

/**
 * Wrapper for pages that require a single unit selected.
 * When the user is in "All units" mode (selectedUnitId === null),
 * shows an empty state instead of rendering the children, preventing
 * downstream stores/fetches from being called with null.
 */
export function RequireUnit({ children, label = 'esta página' }: Props) {
  const selectedUnitId = useUnitStore((s) => s.selectedUnitId)
  const units = useUnitStore((s) => s.units)
  const selectUnit = useUnitStore((s) => s.selectUnit)
  const activeUnits = units.filter((u) => u.active !== false)

  if (selectedUnitId === null) {
    return (
      <div className="rounded-xl border border-border-color bg-surface p-8 text-center">
        <div className="mx-auto max-w-md space-y-3">
          <div className="text-lg font-semibold text-foreground">
            Selecione uma unidade
          </div>
          <div className="text-sm text-foreground-muted">
            Para acessar {label}, escolha a unidade:
          </div>
          {activeUnits.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 pt-1" data-testid="require-unit-picker">
              {activeUnits.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => selectUnit(u.id)}
                  className="min-h-[44px] rounded-lg border border-border-color bg-surface-alt px-4 py-2 text-sm font-medium text-foreground hover:border-accent hover:text-accent"
                >
                  {u.name}
                </button>
              ))}
            </div>
          )}
          <div className="text-xs text-foreground-muted">
            Você está no modo "Todas as unidades" — esta tela trabalha com uma unidade por vez. Também dá para trocar no seletor do topo.
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
